import React, { useEffect, useState } from 'react';
import { getReviews, updateReviewStatus } from '../../services/api';

const card = { background: 'rgba(30,41,59,0.3)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '1rem' };
const input = { width: '100%', padding: '0.6rem 0.8rem', borderRadius: '0.65rem', background: 'rgba(15,23,42,0.5)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', outline: 'none' };
const statusOptions = ['pending', 'approved', 'rejected', 'hidden'];

export default function Reviews() {
  const [reviews, setReviews] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState('');
  const [error, setError] = useState('');
  const [opsFilter, setOpsFilter] = useState('all');

  const load = async (status = statusFilter) => {
    try {
      setLoading(true);
      setError('');
      const data = await getReviews(status ? { status } : {});
      setReviews(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load reviews');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load('');
  }, []);

  const onChangeStatus = async (reviewId, nextStatus) => {
    try {
      setSavingId(reviewId);
      await updateReviewStatus(reviewId, nextStatus);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update review status');
    } finally {
      setSavingId('');
    }
  };

  const filteredReviews = reviews.filter((r) => {
    if (opsFilter === 'needs_consent') return !r.consentToPublish;
    if (opsFilter === 'pending_moderation') return r.status === 'pending' && !!r.consentToPublish;
    if (opsFilter === 'approved_live') return r.status === 'approved' && !!r.consentToPublish;
    return true;
  });

  const exportApprovedCsv = async () => {
    try {
      setError('');
      const rows = await getReviews({ status: 'approved' });
      const approved = (Array.isArray(rows) ? rows : []).filter((r) => r.consentToPublish);
      const headers = ['client_name', 'client_email', 'project_name', 'rating', 'review_text', 'display_name', 'reviewed_at'];
      const lines = [
        headers.join(','),
        ...approved.map((r) => ([
          JSON.stringify(r.client?.name || ''),
          JSON.stringify(r.client?.email || ''),
          JSON.stringify(r.project?.name || ''),
          Number(r.rating || 0),
          JSON.stringify(r.reviewText || ''),
          JSON.stringify(r.displayName || ''),
          JSON.stringify(r.reviewedAt || r.createdAt || ''),
        ].join(','))),
      ];
      const csv = lines.join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `approved-testimonials-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to export CSV');
    }
  };

  return (
    <div style={{ padding: '2rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700, margin: 0 }}>Reviews ({reviews.length})</h1>
        <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center' }}>
          <select
            value={opsFilter}
            onChange={(e) => setOpsFilter(e.target.value)}
            style={{ ...input, width: '14rem' }}
          >
            <option value="all">All (ops view)</option>
            <option value="needs_consent">Needs consent</option>
            <option value="pending_moderation">Pending moderation</option>
            <option value="approved_live">Approved live</option>
          </select>
          <select
            value={statusFilter}
            onChange={async (e) => {
              const value = e.target.value;
              setStatusFilter(value);
              await load(value);
            }}
            style={{ ...input, width: '12rem' }}
          >
            <option value="">All statuses</option>
            {statusOptions.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <button type="button" onClick={exportApprovedCsv} style={{ ...input, width: 'auto', cursor: 'pointer' }}>Export Approved CSV</button>
          <button type="button" onClick={() => load()} style={{ ...input, width: 'auto', cursor: 'pointer' }}>Refresh</button>
        </div>
      </div>

      {error ? <div style={{ marginBottom: '1rem', color: '#f87171', fontSize: '0.9rem' }}>{error}</div> : null}

      <div style={{ ...card, overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 0.6fr 1.5fr 0.8fr', gap: '0.75rem', padding: '0.85rem 1rem', borderBottom: '1px solid rgba(255,255,255,0.08)', color: '#94a3b8', fontSize: '0.82rem' }}>
          <span>Client / Project</span>
          <span>Display Name</span>
          <span>Rating</span>
          <span>Review</span>
          <span>Status</span>
        </div>

        {loading ? (
          <div style={{ padding: '1rem', color: '#94a3b8' }}>Loading reviews...</div>
        ) : filteredReviews.length === 0 ? (
          <div style={{ padding: '1rem', color: '#94a3b8' }}>No reviews found.</div>
        ) : (
          filteredReviews.map((r) => (
            <div key={r._id} style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 0.6fr 1.5fr 0.8fr', gap: '0.75rem', padding: '0.85rem 1rem', borderBottom: '1px solid rgba(255,255,255,0.05)', alignItems: 'start' }}>
              <div>
                <div style={{ fontWeight: 600 }}>{r.client?.name || 'Client'}</div>
                <div style={{ color: '#94a3b8', fontSize: '0.8rem' }}>{r.project?.name || 'No project'}</div>
              </div>
              <div style={{ fontSize: '0.88rem' }}>{r.displayName || '-'}</div>
              <div style={{ fontSize: '0.88rem' }}>{Number(r.rating || 0)}/5</div>
              <div style={{ fontSize: '0.88rem', color: '#cbd5e1' }}>{r.reviewText}</div>
              <div>
                <select
                  value={r.status}
                  onChange={(e) => onChangeStatus(r._id, e.target.value)}
                  disabled={savingId === r._id}
                  style={{ ...input, minWidth: '8rem', opacity: savingId === r._id ? 0.7 : 1 }}
                >
                  {statusOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <div style={{ marginTop: '0.35rem', color: '#94a3b8', fontSize: '0.72rem' }}>
                  {r.consentToPublish ? 'Consent: yes' : 'Consent: no'}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
